import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, DatesRangeValue, DateValue } from "@mantine/dates";
import type { IFormikProps, IProps } from "./date-picker-input.types";
import { Input } from "../input";
import { Popover } from "../popover";
import { faCalendar, faChevronLeft, faChevronRight, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "../icon";
import { DropdownIcon } from "../dropdown-icon";
import { format } from "date-fns";
import { ActionIcon } from "../action-icon";
import { Group } from "../group";
import { Menu } from "../menu";
import {
    addCustomDayProps,
    formatDate,
    getDateTimeValue,
    getMaxDate,
    getMinDate,
    getRelativeDatesInfo,
} from "./date-picker-input.utilities";
import { Text } from "../text";
import { TimePicker } from "../time-picker";
import styles from "./date-picker-input.module.scss";
import { MISSING_END_DATE_TOOLTIP, RelativeDates, TIME_RANGE_TOOLTIP } from "./date-picker-input.constants";
import { useField, useFormikContext } from "formik";
import { kebabCase } from "lodash";
import { Stack } from "../stack";
import { ScrollArea } from "../scroll-area";
import { Button } from "../button";
import { Box } from "../box";
import { Tooltip } from "../tooltip";
import dayjs from "dayjs";

/**
 * DatePickerInput component allows users to select a date range with optional time selection.
 * It supports relative date presets and custom date ranges.
 *
 * @param {IProps} props - The properties for the DatePickerInput component.
 * @param {Date} props.startDate - The initial start date of the range.
 * @param {Date} props.endDate - The initial end date of the range.
 * @param {number} [props.limitSelectionMonths] - The maximum number of months allowed for selection.
 * @param {number} [props.limitPastDays] - The number of past days to limit the selection.
 * @param {RelativeDates} [props.relativeDateTime] - The relative date preset to use.
 * @param {boolean} [props.showRelativeDateValues] - Whether to show relative date presets.
 * @param {boolean} [props.allowTimeSelect]
 * @param {Date} [props.minDate] - The minimum date allowed for selection.
 * @param {Date} [props.maxDate] - The maximum date allowed for selection.
 * @param {RelativeDates[]} [props.omittedRelativeDates] - The relative dates to omit from the dropdown.
 * @returns {JSX.Element} The rendered DatePickerInput component.
 */
const DatePickerInput = ({
    startDate,
    endDate,
    limitSelectionMonths,
    limitPastDays,
    relativeDateTime,
    showRelativeDateValues,
    allowTimeSelect,
    startDateName = "startDateTime",
    endDateName = "endDateTime",
    relativeDateName = "relativeDateTime",
    variant,
    disabled,
    activable,
    onChange,
    displayIcon = true,
    minDate,
    maxDate = new Date(),
    infoLabel,
    transformRelativeDateTime = false,
    omittedRelativeDates,
}: IProps) => {
    const [opened, setOpened] = useState(false);
    const { setFieldValue } = useFormikContext();

    const [value, setValue] = useState<DatesRangeValue<DateValue>>([new Date(), null]);

    const [selectedRelativeDateTime, setSelectedRelativeDateTime] = useState<RelativeDates | null>(null);

    const [startTime, setStartTime] = useState<string | undefined>(undefined);
    const [endTime, setEndTime] = useState<string | undefined>(undefined);

    const [calendarDate, setCalendarDate] = useState<Date>(() =>
        startDate ? new Date(startDate) : dayjs().subtract(1, "month").toDate()
    );

    const onPresetSelect = useCallback((label: RelativeDates) => {
        const presetRange = getRelativeDatesInfo()[label];
        setValue([presetRange.startDate, presetRange.endDate]);
        setSelectedRelativeDateTime(label);
        setCalendarDate(presetRange.startDate);
        setStartTime(format(presetRange.startDate, "HH:mm"));
        setEndTime(format(presetRange.endDate, "HH:mm"));
    }, []);

    const setInitialDate = useCallback(() => {
        // Set to the original startDate, endDate & times or to default values
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            setValue([start, end]);
            setCalendarDate(start);
            setStartTime(format(start, "HH:mm"));
            setEndTime(format(end, "HH:mm"));
        } else {
            const { startDate, endDate } = relativeDateTime
                ? getRelativeDatesInfo()[relativeDateTime]
                : getRelativeDatesInfo().Last14Days;
            setValue([startDate, endDate]);
            setCalendarDate(startDate);
            setStartTime(format(startDate, "HH:mm"));
            setEndTime(format(endDate, "HH:mm"));
        }
    }, [endDate, relativeDateTime, startDate]);

    useEffect(() => {
        if (relativeDateTime) setSelectedRelativeDateTime(relativeDateTime);

        setInitialDate();
    }, [relativeDateTime, setInitialDate]);

    const handleDateChange = useCallback((newValue: DatesRangeValue<DateValue>) => {
        setSelectedRelativeDateTime(null);
        if (newValue[0] === null && newValue[1] === null) {
            setValue([new Date(), null]);
        } else {
            setValue(newValue);
        }
    }, []);

    const onCancel = useCallback(() => {
        setInitialDate();
        setOpened(false);
        setSelectedRelativeDateTime(null);
    }, [setInitialDate]);

    const displayText = useMemo(() => {
        if (relativeDateTime) {
            return getRelativeDatesInfo()[relativeDateTime].formatedName;
        }

        const formattedStartDate = startDate ? format(new Date(startDate), "dd/MM/yy") : "";
        const formattedEndDate = endDate ? format(new Date(endDate), "dd/MM/yy") : "";
        return `${formattedStartDate} - ${formattedEndDate}`;
    }, [startDate, endDate, relativeDateTime]);

    const relativeDatesToShow = useMemo(() => {
        return (Object.keys(getRelativeDatesInfo()) as RelativeDates[]).filter(
            (label) => !omittedRelativeDates?.includes(label)
        );
    }, [omittedRelativeDates]);

    // One resolution of the presets per render, shared by the preset list below. Deliberately not
    // memoized and deliberately not hoisted out of the component: getRelativeDatesInfo() resolves
    // against "now", so a single call per render keeps every preset in the list consistent with each
    // other while still tracking the clock across a long-lived render.
    const relativeDatesInfo = getRelativeDatesInfo();

    // The absolute earliest selectable date, computed fresh on every render (mirroring the calendar's
    // own inline getMinDate call below) so presets and calendar days cannot drift apart as real time
    // advances between renders without a prop change. rangeStart is null and limitSelectionMonths is
    // omitted on purpose: we want the absolute floor, not the floor relative to an in-progress selection.
    const presetFloor = getMinDate({
        minDate: minDate ? dayjs(minDate) : undefined,
        limitPastDays,
        rangeStart: null,
    });

    const handleStartTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setStartTime(value);
    }, []);

    const handleEndTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setEndTime(value);
    }, []);

    /** Time range is not valid when:
     * - The start time is after the end time.
     * - The start time is the same as the end time and the start date is the same as the end date.
     */
    const isTimeRangeValid = useMemo(() => {
        if (!allowTimeSelect || !value[0] || !value[1]) return true;
        const start = dayjs(value[0]);
        const end = dayjs(value[1]);
        if (start.isAfter(end, "day")) return false;
        if (start.isSame(end, "day") && startTime === endTime) return false;
        return true;
    }, [allowTimeSelect, value, startTime, endTime]);

    const isWithinDateRestriction = useMemo(() => {
        const startDateTime = getDateTimeValue(value[0], startTime);
        const endDateTime = getDateTimeValue(value[1], endTime, true);

        // Compare at day granularity so end-of-day times (e.g. 23:59) on the
        // current day remain valid even though they are technically "in the
        // future" relative to the current moment when maxDate defaults to now.
        if (startDateTime && minDate && dayjs(startDateTime).isBefore(minDate, "day")) {
            return false;
        }
        if (endDateTime && maxDate && dayjs(endDateTime).isAfter(maxDate, "day")) {
            return false;
        }

        return true;
    }, [endTime, maxDate, minDate, startTime, value]);

    // Whether what the user has actually entered is usable: the times are in order, and the range
    // sits inside any min/max restriction. This deliberately excludes the missing end date, because
    // it drives the red highlight on the time inputs and a half-finished selection is not something
    // the user has got *wrong* -- so neither input should be flagged for it.
    const isDateRangeValid = isTimeRangeValid && isWithinDateRestriction;

    // A range with a start but no end is only ever an in-progress selection: `setInitialDate` and
    // `onPresetSelect` always set both, and `handleDateChange` guarantees value[0] is non-null. Done
    // used to be enabled in this state but committed nothing, silently discarding the start date the
    // user had just picked (GAS-716).
    const isEndDateSelected = value[1] !== null;

    const canApplyDateRange = isEndDateSelected && isDateRangeValid;

    return (
        <Popover
            position="bottom-start"
            shadow="md"
            disabled={disabled}
            opened={opened}
            onExitTransitionEnd={setInitialDate}
            onDismiss={() => setOpened(false)}
        >
            <Popover.Target>
                <Input
                    active={activable && (typeof startTime !== "undefined" || typeof endTime !== "undefined")}
                    leftSection={displayIcon ? <Icon icon={faCalendar} size="sm" /> : undefined}
                    rightSection={<DropdownIcon isOpen={opened} />}
                    component="button"
                    pointer
                    type="button"
                    onClick={() => setOpened(!opened)}
                    data-testid="date-range-filter"
                    variant={variant}
                    disabled={disabled}
                >
                    {displayText}
                </Input>
            </Popover.Target>
            <Popover.Dropdown bdrs="lg" p="0" data-testid="date-picker-container">
                <Group className={styles["relative-dates-container"]} gap="md" bdrs="lg">
                    {showRelativeDateValues && (
                        <Stack className={styles["relative-dates"]} p="sm" gap="0" miw={100}>
                            <Menu>
                                {relativeDatesToShow.map((label) => {
                                    const preset = relativeDatesInfo[label];

                                    return (
                                        <Menu.Item
                                            key={label}
                                            onClick={() => onPresetSelect(label)}
                                            active={selectedRelativeDateTime === label}
                                            disabled={
                                                presetFloor ? dayjs(preset.startDate).isBefore(presetFloor, "day") : false
                                            }
                                            data-testid={`date-options_${kebabCase(label)}`}
                                        >
                                            {preset.formatedName}
                                        </Menu.Item>
                                    );
                                })}
                            </Menu>
                        </Stack>
                    )}

                    <Stack className={styles["date-picker-container"]} p="lg" bdrs="lg">
                        <ScrollArea className={styles["calendar-scroll-area"]}>
                            {typeof limitSelectionMonths !== "undefined" && limitSelectionMonths > 0 && (
                                <Group gap="sm">
                                    <Icon className={styles["info-icon"]} icon={faCircleInfo} size="xxs" />
                                    <Text className={styles["info-text"]} size="xs">
                                        {infoLabel ??
                                            `Maximum range is ${limitSelectionMonths} month${limitSelectionMonths === 1 ? "" : "s"}.`}
                                    </Text>
                                </Group>
                            )}
                            <DatePicker
                                onChange={handleDateChange}
                                value={value}
                                type="range"
                                minDate={getMinDate({
                                    minDate: minDate ? dayjs(minDate) : undefined,
                                    limitPastDays,
                                    limitSelectionMonths,
                                    rangeStart: value[0] !== null && value[1] === null ? dayjs(value[0]) : null,
                                })}
                                maxDate={getMaxDate({
                                    maxDate: dayjs(maxDate ?? new Date()),
                                    limitSelectionMonths,
                                    rangeStart: value[1] === null ? (value[0] !== null ? dayjs(value[0]) : null) : null,
                                })}
                                date={calendarDate}
                                onDateChange={setCalendarDate}
                                previousIcon={
                                    <ActionIcon variant="default" data-testid="previous-month-button">
                                        <Icon icon={faChevronLeft} size="xxs" />
                                    </ActionIcon>
                                }
                                previousLabel="Previous Month"
                                nextIcon={
                                    <ActionIcon variant="default" data-testid="next-month-button">
                                        <Icon icon={faChevronRight} size="xxs" />
                                    </ActionIcon>
                                }
                                nextLabel="Next Month"
                                numberOfColumns={2}
                                getDayAriaLabel={(date) => `date-picker_${kebabCase(formatDate(date))}`}
                                getDayProps={(date) => addCustomDayProps(date)}
                                allowSingleDateInRange
                                classNames={{
                                    day: styles["day"],
                                    monthRow: styles["monthRow"],
                                    monthCell: styles["monthCell"],
                                }}
                            />
                        </ScrollArea>
                        <Group gap="sm" mt="auto" align="flex-end" justify="space-between">
                            {allowTimeSelect && (
                                <Group gap="sm" w={225}>
                                    <Input.Wrapper variant="filled" flex={1} label={formatDate(value[0])}>
                                        <TimePicker
                                            value={startTime}
                                            onChange={handleStartTimeChange}
                                            variant="filled"
                                            data-testid="date-picker-input-start-time"
                                            error={!isDateRangeValid ? true : undefined}
                                        />
                                    </Input.Wrapper>

                                    <Input.Wrapper
                                        variant="filled"
                                        flex={1}
                                        label={value[1] ? formatDate(value[1]) : "No date selected"}
                                    >
                                        <TimePicker
                                            // Showing a concrete time beside a "No date selected"
                                            // label reads as a real value for a date that does not
                                            // exist. Blank the display only -- `endTime` itself is
                                            // kept so completing the range restores it rather than
                                            // silently committing midnight.
                                            value={isEndDateSelected ? endTime : ""}
                                            onChange={handleEndTimeChange}
                                            variant="filled"
                                            data-testid="date-picker-input-end-time"
                                            error={!isDateRangeValid ? true : undefined}
                                        />
                                    </Input.Wrapper>
                                </Group>
                            )}

                            <Group gap="sm" ml="auto">
                                <Button onClick={onCancel} variant="default" data-testid="cancel-button">
                                    Cancel
                                </Button>
                                <Tooltip
                                    label={
                                        !isEndDateSelected
                                            ? MISSING_END_DATE_TOOLTIP
                                            : isTimeRangeValid
                                              ? "Select a date range within the allowed spotlight range."
                                              : TIME_RANGE_TOOLTIP
                                    }
                                    disabled={canApplyDateRange}
                                >
                                    {/* Browsers do not dispatch mouse events from a natively
                                        disabled button, so a Tooltip attached straight to it never
                                        opens -- which is why the existing invalid-time tooltip has
                                        never been visible either. Hang it off a wrapper that still
                                        receives pointer events, as `select-menu` already does for
                                        its disabled items. */}
                                    <Box data-testid="done-button-wrapper">
                                        <Button
                                            onClick={() => {
                                                const startDateTime = getDateTimeValue(value[0], startTime);
                                                const endDateTime = getDateTimeValue(value[1], endTime, true);

                                                setOpened(false);
                                                if (selectedRelativeDateTime) {
                                                    setFieldValue(relativeDateName, selectedRelativeDateTime);
                                                    setFieldValue(
                                                        startDateName,
                                                        transformRelativeDateTime
                                                            ? getRelativeDatesInfo()[selectedRelativeDateTime].startDate
                                                            : undefined
                                                    );
                                                    setFieldValue(
                                                        endDateName,
                                                        transformRelativeDateTime
                                                            ? getRelativeDatesInfo()[selectedRelativeDateTime].endDate
                                                            : undefined
                                                    );
                                                    onChange?.(null, null, selectedRelativeDateTime);
                                                } else if (startDateTime && endDateTime) {
                                                    setFieldValue(startDateName, startDateTime);
                                                    setFieldValue(endDateName, endDateTime);
                                                    setFieldValue(relativeDateName, undefined);
                                                    onChange?.(startDateTime, endDateTime, null);
                                                }
                                            }}
                                            disabled={!canApplyDateRange}
                                            data-testid="done-button"
                                        >
                                            Done
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Group>
                        </Group>
                    </Stack>
                </Group>
            </Popover.Dropdown>
        </Popover>
    );
};

const DatePickerInputFormik: FunctionComponent<IFormikProps> = ({ validate, ...otherProps }) => {
    const [startDateField] = useField(otherProps.startDateName);
    const [endDateField] = useField(otherProps.endDateName);
    const [relativeDateField] = useField(otherProps.relativeDateName ?? "relativeDateTime");
    return (
        <Input.Formik.Wrapper name={otherProps.startDateName} validate={validate}>
            <DatePickerInput
                startDate={startDateField.value}
                endDate={endDateField.value}
                relativeDateTime={relativeDateField.value}
                {...otherProps}
            />
        </Input.Formik.Wrapper>
    );
};

DatePickerInput.Formik = DatePickerInputFormik;

export { DatePickerInput };

export default DatePickerInput;
